import { ROUTES, routeByPath, type Route } from './routes'
import { session } from './session.svelte'

/*
 * A history-API router over the route table.
 *
 * Small on purpose. Every route is a real HTML file uploaded extensionless (see routes.ts), so a deep
 * link is served directly by storage and this only handles navigation WITHIN the site. There is no
 * pattern matching and no nesting to support — eight flat routes.
 */

const SIGN_IN = ROUTES.find((route) => route.name === 'sign-in')!
const HOME = ROUTES.find((route) => route.name === 'summary')!

let currentPath = $state(location.pathname)

/**
 * Where to return to after signing in. Held in memory rather than in the URL: a `?returnUrl=` parameter
 * is an open-redirect surface that has to be validated on the way out, and there is nothing here worth
 * that. Losing it on a refresh costs one click.
 */
let intended: string | null = null

const resolve = (path: string): Route => routeByPath(path) ?? HOME

export const router = {
  get route(): Route {
    const route = resolve(currentPath)
    // The guard lives in the getter rather than in navigation, so it also covers a deep link, a refresh
    // and a session expiring under an open tab — not only clicks.
    if (!route.anonymous && !session.signedIn) return SIGN_IN
    // Somebody already signed in has no use for the sign-in page; send them home rather than showing a
    // form that would sign them in as themselves again.
    if (route.name === 'sign-in' && session.signedIn) return HOME
    return route
  },

  get path() {
    return currentPath
  },

  go(path: string, replace = false) {
    if (path === currentPath) return
    history[replace ? 'replaceState' : 'pushState']({}, '', path)
    currentPath = path
    // Matches a real navigation: a browser puts you at the top of the next page.
    window.scrollTo(0, 0)
  },

  /** Called when a page decides the customer must sign in — a 401, or signing out. */
  requireSignIn() {
    if (resolve(currentPath).anonymous) return
    intended = currentPath
    router.go(SIGN_IN.path, true)
  },

  /**
   * After a successful sign-in: back to where they were heading, or the account home.
   *
   * TWO WAYS to arrive at the sign-in page, and only one of them records an intention. `requireSignIn`
   * navigates and remembers; the guard in `route` above shows the sign-in form WITHOUT changing the URL,
   * so for a deep link the destination is simply where we already are. Reading `intended ?? currentPath`
   * covers both — and a bare `intended ?? HOME` did not, which sent anyone following a link from an
   * email to the account home instead of to the page the link named.
   */
  resume() {
    const here = routeByPath(currentPath)
    const target = intended ?? (here && !here.anonymous ? currentPath : HOME.path)
    intended = null
    // A no-op when already there, which is the deep-link case: the route getter recomputes anyway,
    // because the guard it applies reads the session that has just changed.
    router.go(target, true)
  },
}

/** Back and forward have to move the app, or the buttons appear broken. */
addEventListener('popstate', () => {
  currentPath = location.pathname
})

/*
 * One delegated listener for every in-site link, so no component has to remember to call the router.
 * Anything this does not claim — another origin, a download, a new tab, a modified click — is left to
 * the browser, which is what keeps middle-click and cmd-click working.
 */
addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const anchor = (event.target as Element | null)?.closest?.('a')
  if (!anchor) return
  if (anchor.target || anchor.hasAttribute('download') || anchor.hasAttribute('data-external')) return

  const href = anchor.getAttribute('href')
  if (!href || href.startsWith('#')) return

  const url = new URL(href, location.href)
  if (url.origin !== location.origin) return
  if (!routeByPath(url.pathname)) return

  event.preventDefault()
  router.go(url.pathname + url.search)
})
